import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Tabs, Spin, Table } from "antd";
import {
  PrinterOutlined,
  FilePdfOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { fetchIncomeStatementThunk, fetchBalanceSheetThunk } from "../../../../Store/Slices/Finance/financialStatements/financialStatements.thunk";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { fetchCashFlowsThunk } from "../../../../Store/Slices/Finance/cashFlow/cashFlow.thunk";
import TableShimmer from "../../../../Components/Common/TableShimmer.jsx";
import { createRoot } from "react-dom/client";
import FinancialReportTemplate from "./FinancialReportTemplate";
import { useNavigate } from "react-router-dom";


const { TabPane } = Tabs;

const filters = [
  { label: "This Month", value: "thisMonth" },
  { label: "Last Month", value: "lastMonth" },
  { label: "Quarter", value: "quarter" },
  { label: "Year", value: "year" },
];

// Simple SVG sparkline
const Sparkline = ({ color }) => (
  <svg
    viewBox="0 0 120 40"
    className="w-full h-16 mt-3"
    preserveAspectRatio="none"
  >
    <defs>
      <linearGradient id={`grad-${color}`} x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor={color} stopOpacity="0.3" />
        <stop offset="100%" stopColor={color} stopOpacity="0.02" />
      </linearGradient>
    </defs>
    <path
      d="M0,30 C20,28 30,18 50,20 C70,22 80,10 120,8"
      fill="none"
      stroke={color}
      strokeWidth="2"
    />
    <path
      d="M0,30 C20,28 30,18 50,20 C70,22 80,10 120,8 L120,40 L0,40 Z"
      fill={`url(#grad-${color})`}
    />
  </svg>
);

const formatCurrency = (currency, amount) => {
  const num = Number(amount || 0);
  return `${currency + " " || "BHD "}${num.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}`;
};

const transactionTypeOptions = [
  { value: "cash_to_account", label: "Cash → Account", fromType: "cash", toType: "account" },
  { value: "account_to_cash", label: "Account → Cash", fromType: "account", toType: "cash" },
  { value: "account_to_account", label: "Account → Account", fromType: "account", toType: "account" },
  { value: "check_to_account", label: "Check → Account", fromType: "check", toType: "account" },
  { value: "check_to_cash", label: "Check → Cash", fromType: "check", toType: "cash" },
  { value: "account_to_check", label: "Account → Check", fromType: "account", toType: "check" },
];

const typeLabels = {
  cash: "Cash",
  account: "Account",
  check: "Check",
};

const typeColors = {
  cash: "bg-green-50 text-green-600 border-green-100",
  account: "bg-blue-50 text-blue-600 border-blue-100",
  check: "bg-orange-50 text-orange-600 border-orange-100",
};

const txnColors = {
  cash_to_account: "bg-purple-50 text-purple-600 border-purple-100",
  account_to_cash: "bg-indigo-50 text-indigo-600 border-indigo-100",
  account_to_account: "bg-blue-50 text-blue-600 border-blue-100",
  check_to_account: "bg-yellow-50 text-yellow-600 border-yellow-100",
  check_to_cash: "bg-orange-50 text-orange-600 border-orange-100",
  account_to_check: "bg-pink-50 text-pink-600 border-pink-100",
};

const MainSection = () => {
  const { t } = useTranslation("prtFinance");
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [activeFilter, setActiveFilter] = useState("thisMonth");
  const [activeTab, setActiveTab] = useState("1");
  const { cashFlows, loading: cashFlowLoading } = useSelector((s) => s.admin.cashFlow);
  const { incomeStatement, loading, balanceSheet, balanceSheetLoading } = useSelector(
    (state) => state.admin.financialStatements
  );
  const currency = useSelector(
    (state) => state.common.user.userDetails?.currency
  );
  const schoolDetails = useSelector(
    (state) => state.common.user.userDetails
  );

  useEffect(() => {
    dispatch(fetchIncomeStatementThunk(activeFilter));
  }, [dispatch, activeFilter]);

  useEffect(() => {
    if (activeTab === "2") {
      dispatch(fetchBalanceSheetThunk());
    }
  }, [dispatch, activeTab]);
  useEffect(() => {
    dispatch(
      fetchCashFlowsThunk({
        page: 1,
        limit: 5,
        filter: activeFilter,
      })
    );
  }, [dispatch, activeFilter]);

  const handleDownloadPDF = async (type) => {
    const container = document.createElement("div");
    container.style.position = "fixed";
    container.style.left = "-9999px";
    container.style.top = "0";
    container.style.width = "794px"; // A4 width at 96dpi
    document.body.appendChild(container);

    const root = createRoot(container);

    // Setup the correct data payload based on the report type
    let reportData;
    if (type === "income") {
      reportData = {
        totalRevenue,
        totalExpenses,
        netIncome,
        payroll,
        generalExpenses,
        reportPeriod,
        generatedAt,
      };
    } else if (type === "balancesheet") {
      reportData = {
        balanceSheet, // Passes the balanceSheet from Redux
        reportPeriod,
        generatedAt,
      };
    } else {
      reportData = {
        flows: cashFlows,
        reportPeriod,
        generatedAt,
      };
    }

    root.render(
      <FinancialReportTemplate
        type={type}
        data={reportData}
        school={{ ...schoolDetails, currency }}
      />
    );

    await new Promise((r) => setTimeout(r, 600));

    const canvas = await html2canvas(container, { scale: 2, useCORS: true });
    const imgData = canvas.toDataURL("image/png");

    const pdf = new jsPDF("p", "mm", "a4");
    const pageWidth = 210;
    const pageHeight = 297;
    const imgHeightMm = (canvas.height / canvas.width) * pageWidth;

    if (imgHeightMm <= pageHeight) {
      pdf.addImage(imgData, "PNG", 0, 0, pageWidth, imgHeightMm);
    } else {
      let yOffset = 0;
      const pxPerMm = canvas.width / pageWidth;
      while (yOffset < canvas.height) {
        const sliceHeight = Math.min(pageHeight * pxPerMm, canvas.height - yOffset);
        const sliceCanvas = document.createElement("canvas");
        sliceCanvas.width = canvas.width;
        sliceCanvas.height = sliceHeight;
        const ctx = sliceCanvas.getContext("2d");
        ctx.drawImage(canvas, 0, -yOffset);
        const sliceData = sliceCanvas.toDataURL("image/png");
        if (yOffset > 0) pdf.addPage();
        pdf.addImage(sliceData, "PNG", 0, 0, pageWidth, (sliceHeight / canvas.width) * pageWidth);
        yOffset += sliceHeight;
      }
    }

    // Assign the right file name
    let fileName = "financial_statement.pdf";
    if (type === "income") fileName = "income_statement.pdf";
    else if (type === "balancesheet") fileName = "balance_sheet.pdf";
    else fileName = "cash_flow_statement.pdf";

    pdf.save(fileName);

    root.unmount();
    document.body.removeChild(container);
  };

  const totalRevenue = incomeStatement?.revenue?.total ?? 0;
  const totalExpenses = incomeStatement?.expenses?.total ?? 0;
  const netIncome = incomeStatement?.netOperatingIncome ?? 0;
  const payroll = incomeStatement?.expenses?.payroll ?? 0;
  const generalExpenses = incomeStatement?.expenses?.general ?? 0;

  const now = new Date();
  const reportPeriod = now.toLocaleString("en-US", {
    month: "short",
    year: "numeric",
  });
  const generatedAt = now.toLocaleString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });

  const summaryText =
    netIncome < 0
      ? t(
        `The financial performance for ${reportPeriod} shows a negative net result.Operational costs are currently higher than revenue inflow.`
      )
      : t(
        `The financial performance for ${reportPeriod} shows a positive net result. Revenue is exceeding operational costs.`
      );

  const columns = [
    {
      title: (
        <span className="text-xs font-semibold uppercase tracking-widest text-gray-400">
          Type
        </span>
      ),
      dataIndex: "transactionType",
      key: "transactionType",
      render: (value) => {
        const opt = transactionTypeOptions.find((t) => t.value === value);
        return (
          <span
            className={`inline-flex px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border ${txnColors[value] || "bg-gray-50 text-gray-600 border-gray-100"
              }`}
          >
            {opt?.label || value || "—"}
          </span>
        );
      },
    },
    {
      title: (
        <span className="text-xs font-semibold uppercase tracking-widest text-gray-400">
          From
        </span>
      ),
      dataIndex: "from",
      key: "from",
      render: (from) => {
        if (!from) return <span className="text-sm text-gray-400">—</span>;
        const entityName = from.entityId?.entityName;
        return (
          <div className="flex flex-col gap-0.5">
            <span
              className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border w-fit ${typeColors[from.type] || "bg-gray-50 text-gray-600 border-gray-100"
                }`}
            >
              {typeLabels[from.type] || from.type || "—"}
            </span>
            {entityName && (
              <span className="text-xs text-gray-500">{entityName}</span>
            )}
          </div>
        );
      },
    },
    {
      title: (
        <span className="text-xs font-semibold uppercase tracking-widest text-gray-400">
          To
        </span>
      ),
      dataIndex: "to",
      key: "to",
      render: (to) => {
        if (!to) return <span className="text-sm text-gray-400">—</span>;
        const entityName = to.entityId?.entityName;
        return (
          <div className="flex flex-col gap-0.5">
            <span
              className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border w-fit ${typeColors[to.type] || "bg-gray-50 text-gray-600 border-gray-100"
                }`}
            >
              {typeLabels[to.type] || to.type || "—"}
            </span>
            {entityName && (
              <span className="text-xs text-gray-500">{entityName}</span>
            )}
          </div>
        );
      },
    },
    {
      title: (
        <span className="text-xs font-semibold uppercase tracking-widest text-gray-400">
          Amount
        </span>
      ),
      dataIndex: "amount",
      key: "amount",
      render: (value) => (
        <span className="text-sm font-semibold text-gray-800">
          {value != null ? Number(value).toLocaleString() : "—"}
        </span>
      ),
    },
    {
      title: (
        <span className="text-xs font-semibold uppercase tracking-widest text-gray-400">
          Date
        </span>
      ),
      dataIndex: "transactionDate",
      key: "transactionDate",
      render: (value) => (
        <span className="text-sm text-gray-500">
          {value ? dayjs(value).format("DD MMM YYYY") : "—"}
        </span>
      ),
    },
    {
      title: (
        <span className="text-xs font-semibold uppercase tracking-widest text-gray-400">
          Note
        </span>
      ),
      dataIndex: "note",
      key: "note",
      ellipsis: true,
      render: (value) => (
        <span className="text-sm text-gray-500">{value || "—"}</span>
      ),
    },

  ];

  return (
    <div className="p-4 space-y-6 bg-gray-50 min-h-screen print-section">
      {/* HEADER */}
      <div className="flex flex-wrap justify-between items-center gap-3 bg-white p-4 rounded-xl shadow-sm mb-4">
        <div>
          <h1 className="page-heading mb-0">{t("Financial Statements")}</h1>
          <p className="subheading text-base">
            {t("Track financial performance and actionable insights")}
          </p>
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          {/* Filter tabs */}
          <div className="bg-gray-200 rounded-2xl p-1 flex shadow-sm">
            {filters.map((f) => (
              <button
                key={f.value}
                onClick={() => setActiveFilter(f.value)}
                className={`px-4 py-1 rounded-full text-sm font-medium transition ${activeFilter === f.value
                  ? "bg-white text-purple-600 shadow-sm"
                  : "text-gray-500 hover:text-gray-700"
                  }`}
              >
                {t(f.label)}
              </button>
            ))}
          </div>

          <button
            onClick={() => window.print()}
            className="no-print btn-gradient text-white py-2 px-4 flex items-center gap-2 text-sm"
          >
            <PrinterOutlined />{t("Print Financial Report")}
          </button>
        </div>
      </div>

      {/* KPI CARDS */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Total Revenue */}
        <div className="bg-white rounded-xl p-4 border-x-2 border-gray-100 shadow-sm">
          <div className="flex justify-between text-sm">
            <span className="text-gray-500 font-medium tracking-wide">
              {t("TOTAL REVENUE")}
            </span>
            {/* <span className="text-green-500 font-medium flex items-center gap-1">
              <RiseOutlined /> +12.5%
            </span> */}
          </div>
          <h2 className="text-4xl font-bold mt-2">
            {loading ? (
              <Spin size="small" />
            ) : (
              formatCurrency(currency, totalRevenue)
            )}
          </h2>
          <Sparkline color="#22c55e" />
        </div>

        {/* Total Expenses */}
        <div className="bg-white rounded-xl p-4 shadow-sm">
          <div className="flex justify-between text-sm">
            <span className="text-gray-500 font-medium tracking-wide">
              {t("TOTAL EXPENSES")}
            </span>
            {/* <span className="text-red-500 font-medium flex items-center gap-1">
              <FallOutlined /> -5.2%
            </span> */}
          </div>
          <h2 className="text-3xl font-bold mt-2">
            {loading ? (
              <Spin size="small" />
            ) : (
              formatCurrency(currency, totalExpenses)
            )}
          </h2>
          <Sparkline color="#ef4444" />
        </div>

        {/* Net Income */}
        <div className="bg-white rounded-xl p-4 shadow-sm">
          <div className="flex justify-between text-sm">
            <span className="text-gray-500 font-medium tracking-wide">
              {t("NET INCOME")}
            </span>
            {/* <span className="text-purple-500 font-medium flex items-center gap-1">
              <RiseOutlined /> +18.4%
            </span> */}
          </div>
          <h2 className="text-3xl font-bold mt-2">
            {loading ? (
              <Spin size="small" />
            ) : (
              formatCurrency(currency, netIncome)
            )}
          </h2>
          <Sparkline color="#a855f7" />
        </div>
      </div>

      {/* TABS */}
      <Tabs
        activeKey={activeTab}
        onChange={(key) => setActiveTab(key)}
        className="custom-tabs"
      >

        <TabPane tab={t("INCOME STATEMENT")} key="1">

            {loading ? (
              <div className="flex justify-center py-20">
                <Spin size="large" />
              </div>
            ) : (
              <div className="bg-white rounded-xl p-5 shadow-sm pdf-section">
                {/* Statement header */}
                <div className="flex justify-between items-center border-b pb-4 mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-14 h-14 rounded-2xl bg-purple-100 flex items-center justify-center">
                      <FilePdfOutlined className="text-purple-600" />
                    </div>
                    <div>
                      <h2 className="text-lg font-semibold">
                        {t("Income Statement")}
                      </h2>
                      <p className="text-sm text-gray-500">
                        {t("Report Period")}: {reportPeriod} •{" "}
                        {t("Generated")}: {generatedAt}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2 items-center">
                    <button
                      onClick={() => handleDownloadPDF("income")}
                      className="no-print btn-gradient flex items-center gap-2 px-3 py-1 text-white rounded-lg text-xs"
                    >
                      <FilePdfOutlined />
                      Download PDF
                    </button>

                    <span className="text-green-600 font-semibold text-xs">
                      REAL-TIME SYNC
                    </span>
                  </div>
                </div>

                {/* Summary + Recommendations */}
                <div className="grid md:grid-cols-2 gap-4 mb-6">
                  <div>
                    <h3 className="text-xs text-gray-400 font-semibold mb-2 tracking-widest">
                      {t("SUMMARY ANALYSIS")}
                    </h3>
                    <p className="text-sm text-gray-600">{summaryText}</p>
                  </div>

                  <div>
                    <h3 className="text-xs text-gray-400 font-semibold mb-2 tracking-widest">
                      {t("KEY RECOMMENDATIONS")}
                    </h3>
                    <ul className="text-sm text-gray-600 list-disc pl-4 space-y-1">
                      <li>{t("Review operational expenses for potential cuts.")}</li>
                      <li>{t("Monitor fee collection rates closely.")}</li>
                      <li>
                        {t("Evaluate asset depreciation impact on long-term equity.")}
                      </li>
                    </ul>
                  </div>
                </div>

                {/* Detailed Breakdown */}
                <div className="border-t pt-4">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-sm font-bold text-gray-700 tracking-widest uppercase">
                      {t("DETAILED BREAKDOWN")}
                    </h3>
                    <span className="text-xs text-gray-400">
                      {t("All figures in")} {currency || "BHD"}
                    </span>
                  </div>

                  <div className="space-y-0 text-sm divide-y divide-gray-100">
                    {/* Operating Revenue */}
                    <div className="flex justify-between items-center py-3 font-semibold text-gray-800">
                      <span>{t("Operating Revenue")}</span>
                      <span className="font-bold">
                        {formatCurrency(currency, totalRevenue)}
                      </span>
                    </div>


                    <div className="flex justify-between items-center py-3 pl-4 text-gray-500">
                      <span>• {t("Other Income")}</span>
                      <span>{formatCurrency(currency, 0)}</span>
                    </div>

                    {/* Operating Expenses */}
                    <div className="flex justify-between items-center py-3 font-semibold text-red-500">
                      <span>{t("Operating Expenses")}</span>
                      <span>
                        ({formatCurrency(currency, totalExpenses)})
                      </span>
                    </div>

                    <div className="flex justify-between items-center py-3 pl-4 text-gray-500">
                      <span>• {t("Payroll & Benefits")}</span>
                      <span>{formatCurrency(currency, payroll)}</span>
                    </div>

                    <div className="flex justify-between items-center py-3 pl-4 text-gray-500">
                      <span>• {t("General Expenses")}</span>
                      <span>{formatCurrency(currency, generalExpenses)}</span>
                    </div>

                    {/* Net Operating Income */}
                    <div className="flex justify-between items-center py-4 font-bold text-base text-gray-900">
                      <span>{t("Net Operating Income")}</span>
                      <span
                        className={
                          netIncome >= 0 ? "text-green-600" : "text-red-500"
                        }
                      >
                        {formatCurrency(currency, netIncome)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}
        </TabPane>

        <TabPane tab={t("BALANCE SHEET")} key="2">
          <div className="bg-white rounded-xl p-5 shadow-sm pdf-section">
            {/* Header */}
            <div className="flex justify-between items-center border-b pb-4 mb-6">
              <div className="flex items-center gap-3">
                <div className="w-14 h-14 rounded-2xl bg-purple-100 flex items-center justify-center">
                  <FilePdfOutlined className="text-purple-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold">{t("Balance Sheet")}</h2>
                  <p className="text-sm text-gray-500">
                    {t("As Of")}: {generatedAt}
                    {balanceSheet?.financialYear && (
                      <> • {t("FY")}: {dayjs(balanceSheet.financialYear.start).format("MMM YYYY")} – {dayjs(balanceSheet.financialYear.end).format("MMM YYYY")}</>
                    )}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleDownloadPDF("balancesheet")}
                  className="no-print btn-gradient flex items-center gap-2 px-3 py-1 text-white rounded-lg text-xs"
                >
                  <FilePdfOutlined />
                  {t("Download PDF")}
                </button>
                <span className="text-green-600 font-semibold text-xs">REAL-TIME SYNC</span>
              </div>
            </div>

            {balanceSheetLoading ? (
              <div className="flex justify-center py-20"><Spin size="large" /></div>
            ) : !balanceSheet ? (
              <div className="flex flex-col items-center justify-center py-16 text-gray-400 gap-3">
                <FilePdfOutlined style={{ fontSize: 40, opacity: 0.3 }} />
                <p className="text-sm">{t("No data available")}</p>
              </div>
            ) : (
              <>
                {/* ── Today KPI ── */}
                <div className="mb-6 p-4 rounded-xl bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-100 flex flex-wrap gap-6 items-center">
                  <div>
                    <p className="text-xs text-gray-500 font-semibold uppercase tracking-wider">{t("Today's Student Revenue")}</p>
                    <p className="text-2xl font-bold text-purple-700">{formatCurrency(currency, balanceSheet.assets.currentAssets.todayRevenue)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 font-semibold uppercase tracking-wider">{t("Total Receivable (Unpaid)")}</p>
                    <p className="text-2xl font-bold text-red-500">{formatCurrency(currency, balanceSheet.assets.currentAssets.receivables.totalUnpaid)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 font-semibold uppercase tracking-wider">{t("Total Collected (FY)")}</p>
                    <p className="text-2xl font-bold text-green-600">{formatCurrency(currency, balanceSheet.assets.currentAssets.receivables.totalReceived)}</p>
                  </div>
                  <div className="ml-auto text-right">
                    <p className="text-xs text-gray-500 font-semibold uppercase tracking-wider">{t("Total Assets")}</p>
                    <p className="text-2xl font-bold text-gray-900">{formatCurrency(currency, balanceSheet.assets.totalAssets)}</p>
                  </div>
                </div>

                {/* ── Two-column CA Balance Sheet ── */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

                  {/* ════ LEFT: ASSETS ════ */}
                  <div className="border border-gray-200 rounded-xl overflow-hidden">
                    <div className="bg-gradient-to-r from-pink-600  to-violet-600  border-none text-white px-4 py-2 font-bold text-sm tracking-widest uppercase">
                      {t("ASSETS")}
                    </div>

                    {/* Current Assets */}
                    <div className="px-4 pt-4 pb-2">
                      <p className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-2">{t("I. Current Assets")}</p>

                      {/* Cash & Bank */}
                      <div className="mb-3">
                        <div className="flex justify-between items-center py-1.5 border-b border-dashed border-gray-100">
                          <span className="text-sm font-semibold text-gray-700">{t("Cash & Bank Balances")}</span>
                          <span className="text-sm font-bold text-gray-900">{formatCurrency(currency, balanceSheet.assets.currentAssets.totalCashAndBank)}</span>
                        </div>
                        {/* Cash */}
                        <div className="flex justify-between py-1 pl-4 text-xs text-gray-500">
                          <span>• {t("Cash in Hand")}</span>
                          <span>{formatCurrency(currency, balanceSheet.assets.currentAssets.cash.balance)}</span>
                        </div>
                        <div className="flex justify-between py-0.5 pl-8 text-[10px] text-gray-400">
                          <span>↑ {t("Receipts")}: {formatCurrency(currency, balanceSheet.assets.currentAssets.cash.receiptsIn)}</span>
                          <span>↓ {t("Expenses")}: {formatCurrency(currency, balanceSheet.assets.currentAssets.cash.expensesOut)}</span>
                        </div>
                        {(balanceSheet.assets.currentAssets.cash.transferIn > 0 || balanceSheet.assets.currentAssets.cash.transferOut > 0) && (
                          <div className="flex justify-between py-0.5 pl-8 text-[10px] text-gray-400">
                            <span>↑ {t("Transfers In")}: {formatCurrency(currency, balanceSheet.assets.currentAssets.cash.transferIn)}</span>
                            <span>↓ {t("Transfers Out")}: {formatCurrency(currency, balanceSheet.assets.currentAssets.cash.transferOut)}</span>
                          </div>
                        )}
                        {/* Bank breakdown */}
                        {balanceSheet.assets.currentAssets.bank.breakdown.map((b, i) => (
                          <div key={i} className="pl-4 py-1">
                            <div className="flex justify-between text-xs text-gray-600">
                              <span className="font-medium">• {b.bankName}{b.accountNumber ? ` (…${b.accountNumber.slice(-4)})` : ""}</span>
                              <span className={b.balance >= 0 ? "text-gray-900 font-semibold" : "text-red-500 font-semibold"}>{formatCurrency(currency, b.balance)}</span>
                            </div>
                            <div className="flex gap-3 pl-3 text-[10px] text-gray-400">
                              <span>↑ {t("In")}: {formatCurrency(currency, b.receiptIn)}</span>
                              <span>↓ {t("Out")}: {formatCurrency(currency, b.expenseOut)}</span>
                              {b.transferIn > 0 && <span>⇄ {t("Xfer In")}: {formatCurrency(currency, b.transferIn)}</span>}
                              {b.transferOut > 0 && <span>⇄ {t("Xfer Out")}: {formatCurrency(currency, b.transferOut)}</span>}
                            </div>
                          </div>
                        ))}
                        {balanceSheet.assets.currentAssets.bank.breakdown.length === 0 && (
                          <div className="pl-4 text-xs text-gray-400 py-1">{t("No bank accounts found")}</div>
                        )}
                      </div>

                      {/* Accounts Receivable */}
                      <div className="flex justify-between items-center py-1.5 border-b border-dashed border-gray-100 mb-1">
                        <div>
                          <span className="text-sm font-semibold text-gray-700">{t("Accounts Receivable")}</span>
                          <p className="text-xs text-gray-400">{t("Student fees — outstanding invoices")}</p>
                        </div>
                        <span className="text-sm font-bold text-red-500">{formatCurrency(currency, balanceSheet.assets.currentAssets.receivables.totalUnpaid)}</span>
                      </div>
                      {balanceSheet.assets.currentAssets.receivables.entityRevenue.totalBilled > 0 && (
                        <>
                          <div className="flex justify-between py-1 pl-4 text-xs text-gray-500">
                            <span>• {t("Entity Revenue — Billed")}</span>
                            <span>{formatCurrency(currency, balanceSheet.assets.currentAssets.receivables.entityRevenue.totalBilled)}</span>
                          </div>
                          <div className="flex justify-between py-1 pl-4 text-xs text-gray-500">
                            <span>• {t("Entity Revenue — Received")}</span>
                            <span className="text-green-600">{formatCurrency(currency, balanceSheet.assets.currentAssets.receivables.entityRevenue.totalReceived)}</span>
                          </div>
                        </>
                      )}

                      {/* Inventory */}
                      <div className="mb-3">
                        <div className="flex justify-between items-center py-1.5 border-b border-dashed border-gray-100">
                          <span className="text-sm font-semibold text-gray-700">{t("Inventory (at cost)")}</span>
                          <span className="text-sm font-bold text-gray-900">{formatCurrency(currency, balanceSheet.assets.currentAssets.inventory.total)}</span>
                        </div>
                        {balanceSheet.assets.currentAssets.inventory.breakdown.slice(0, 5).map((item, i) => (
                          <div key={i} className="flex justify-between py-1 pl-4 text-xs text-gray-500">
                            <span>• {item.itemName} ({item.quantity} {t("units")})</span>
                            <span>{formatCurrency(currency, item.value)}</span>
                          </div>
                        ))}
                        {balanceSheet.assets.currentAssets.inventory.breakdown.length > 5 && (
                          <div className="pl-4 text-xs text-gray-400 py-1">
                            +{balanceSheet.assets.currentAssets.inventory.breakdown.length - 5} {t("more items")}
                          </div>
                        )}
                      </div>

                      {/* Current Assets Subtotal */}
                      <div className="flex justify-between items-center py-2 border-t border-gray-200 font-semibold text-sm text-gray-800 mt-1">
                        <span>{t("Total Current Assets")}</span>
                        <span>{formatCurrency(currency, balanceSheet.assets.currentAssets.totalCashAndBank + balanceSheet.assets.currentAssets.receivables.totalUnpaid + balanceSheet.assets.currentAssets.inventory.total)}</span>
                      </div>
                    </div>

                    {/* Non-Current Assets */}
                    <div className="px-4 pb-4 pt-2 border-t border-gray-100">
                      <p className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-2">{t("II. Non-Current Assets")}</p>
                      <div className="mb-2">
                        <div className="flex justify-between items-center py-1.5 border-b border-dashed border-gray-100">
                          <span className="text-sm font-semibold text-gray-700">{t("Property, Plant & Equipment")}</span>
                          <span className="text-sm font-bold text-gray-900">{formatCurrency(currency, balanceSheet.assets.nonCurrentAssets.fixedAssets.total)}</span>
                        </div>
                        {balanceSheet.assets.nonCurrentAssets.fixedAssets.breakdown.map((asset, i) => (
                          <div key={i} className="pl-4 py-1.5 text-xs border-b border-gray-50">
                            <div className="flex justify-between items-start">
                              <div>
                                <span className="text-gray-700 font-medium">{asset.assetName}</span>
                                <span className={`ml-2 px-1.5 py-0.5 rounded text-[10px] font-semibold ${asset.assetValueType === "Depreciating" ? "bg-red-50 text-red-600" :
                                    asset.assetValueType === "Appreciating" ? "bg-green-50 text-green-600" :
                                      "bg-gray-50 text-gray-500"
                                  }`}>
                                  {asset.assetValueType} {asset.ratePerYear > 0 ? `${asset.ratePerYear}%/yr` : ""}
                                </span>
                              </div>
                              <span className="text-gray-900 font-medium">{formatCurrency(currency, asset.currentValue)}</span>
                            </div>
                            <div className="flex gap-3 text-gray-400 mt-0.5">
                              <span>{t("Cost")}: {formatCurrency(currency, asset.originalValue)}</span>
                              <span>{asset.accumulatedChange >= 0 ? "▲" : "▼"} {formatCurrency(currency, Math.abs(asset.accumulatedChange))}</span>
                              <span>{asset.yearsHeld.toFixed(1)} {t("yrs")}</span>
                            </div>
                          </div>
                        ))}
                        {balanceSheet.assets.nonCurrentAssets.fixedAssets.breakdown.length === 0 && (
                          <div className="pl-4 text-xs text-gray-400 py-2">{t("No fixed assets recorded")}</div>
                        )}
                      </div>
                      <div className="flex justify-between items-center py-2 border-t border-gray-200 font-semibold text-sm text-gray-800">
                        <span>{t("Total Non-Current Assets")}</span>
                        <span>{formatCurrency(currency, balanceSheet.assets.nonCurrentAssets.fixedAssets.total)}</span>
                      </div>
                    </div>

                    {/* Total Assets */}
                    <div className="bg-gradient-to-r from-pink-200  to-violet-200 border-t-2 border-purple-600 px-4 py-3 flex justify-between font-bold text-base text-emerald-800">
                      <span>{t("TOTAL ASSETS")}</span>
                      <span>{formatCurrency(currency, balanceSheet.assets.totalAssets)}</span>
                    </div>
                  </div>

                  {/* ════ RIGHT: LIABILITIES + EQUITY ════ */}
                  <div className="border border-gray-200 rounded-xl overflow-hidden">
                    <div className="bg-gradient-to-r from-pink-600  to-violet-600 border-none text-white px-4 py-2 font-bold text-sm tracking-widest uppercase">
                      {t("LIABILITIES & EQUITY")}
                    </div>

                    {/* Equity */}
                    <div className="px-4 pt-4 pb-3">
                      <p className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-2">{t("I. Equity (Shareholders' Funds)")}</p>
                      <div className="flex justify-between items-center py-1.5 border-b border-dashed border-gray-100">
                        <span className="text-sm font-semibold text-gray-700">{t("Share Capital / Investments")}</span>
                        <span className="text-sm font-bold text-gray-900">{formatCurrency(currency, balanceSheet.liabilitiesAndEquity.equity.totalEquity)}</span>
                      </div>
                      {balanceSheet.liabilitiesAndEquity.equity.investments.breakdown.map((inv, i) => (
                        <div key={i} className="flex justify-between py-1 pl-4 text-xs text-gray-500">
                          <span>• {inv.title} ({inv.totalPercentage}%)</span>
                          <span>{formatCurrency(currency, inv.totalInvestment)}</span>
                        </div>
                      ))}
                      {balanceSheet.liabilitiesAndEquity.equity.investments.breakdown.length === 0 && (
                        <div className="pl-4 text-xs text-gray-400 py-1">{t("No investments recorded")}</div>
                      )}
                      <div className="flex justify-between items-center py-2 border-t border-gray-200 font-semibold text-sm text-gray-800 mt-1">
                        <span>{t("Total Equity")}</span>
                        <span>{formatCurrency(currency, balanceSheet.liabilitiesAndEquity.equity.totalEquity)}</span>
                      </div>
                    </div>

                    {/* Non-Current Liabilities */}
                    <div className="px-4 pb-3 border-t border-gray-100">
                      <p className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-2 pt-3">{t("II. Non-Current Liabilities")}</p>
                      <div className="flex justify-between items-center py-1.5 border-b border-dashed border-gray-100">
                        <span className="text-sm font-semibold text-gray-700">{t("Long-term Loans")}</span>
                        <span className="text-sm font-bold text-gray-900">{formatCurrency(currency, balanceSheet.liabilitiesAndEquity.nonCurrentLiabilities.loans.total)}</span>
                      </div>
                      {balanceSheet.liabilitiesAndEquity.nonCurrentLiabilities.loans.breakdown.map((loan, i) => (
                        <div key={i} className="pl-4 py-1.5 text-xs border-b border-gray-50">
                          <div className="flex justify-between">
                            <span className="text-gray-700 font-medium">{loan.title}</span>
                            <span className="text-gray-900 font-medium">{formatCurrency(currency, loan.remainingAmount)}</span>
                          </div>
                          <div className="flex gap-3 text-gray-400 mt-0.5">
                            <span>{t("Principal")}: {formatCurrency(currency, loan.principalAmount)}</span>
                            <span>{loan.interestRate}% {loan.interestType}</span>
                          </div>
                        </div>
                      ))}
                      {balanceSheet.liabilitiesAndEquity.nonCurrentLiabilities.loans.breakdown.length === 0 && (
                        <div className="pl-4 text-xs text-gray-400 py-1">{t("No active loans")}</div>
                      )}
                      <div className="flex justify-between items-center py-2 border-t border-gray-200 font-semibold text-sm text-gray-800 mt-1">
                        <span>{t("Total Non-Current Liabilities")}</span>
                        <span>{formatCurrency(currency, balanceSheet.liabilitiesAndEquity.nonCurrentLiabilities.loans.total)}</span>
                      </div>
                    </div>

                    {/* Current Liabilities */}
                    <div className="px-4 pb-4 border-t border-gray-100">
                      <p className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-2 pt-3">{t("III. Current Liabilities")}</p>

                      <div className="flex justify-between items-center py-1.5 border-b border-dashed border-gray-100">
                        <span className="text-sm text-gray-700">{t("Expenses Payable (General)")}</span>
                        <span className="text-sm font-semibold text-gray-900">{formatCurrency(currency, balanceSheet.liabilitiesAndEquity.currentLiabilities.generalExpensesPayable)}</span>
                      </div>

                      <div className="flex justify-between items-center py-1.5 border-b border-dashed border-gray-100">
                        <span className="text-sm text-gray-700">{t("Salary Payable")}</span>
                        <span className="text-sm font-semibold text-gray-900">{formatCurrency(currency, balanceSheet.liabilitiesAndEquity.currentLiabilities.salaryPayable)}</span>
                      </div>

                      <div className="flex justify-between items-center py-1.5 border-b border-dashed border-gray-100">
                        <div>
                          <span className="text-sm text-gray-700">{t("Vouchers / Cash Transfers")}</span>
                          <span className="ml-2 text-xs text-gray-400">({balanceSheet.liabilitiesAndEquity.currentLiabilities.vouchers.count} {t("entries")})</span>
                        </div>
                        <span className="text-sm font-semibold text-gray-900">{formatCurrency(currency, balanceSheet.liabilitiesAndEquity.currentLiabilities.vouchers.total)}</span>
                      </div>

                      <div className="flex justify-between items-center py-2 border-t border-gray-200 font-semibold text-sm text-gray-800 mt-1">
                        <span>{t("Total Current Liabilities")}</span>
                        <span>{formatCurrency(currency, balanceSheet.liabilitiesAndEquity.currentLiabilities.totalCurrentLiabilities)}</span>
                      </div>
                    </div>

                    {/* Total Liabilities */}
                    <div className="px-4 py-2 bg-gray-50 border-t border-gray-200 flex justify-between font-semibold text-sm text-gray-700">
                      <span>{t("Total Liabilities")}</span>
                      <span>{formatCurrency(currency, balanceSheet.liabilitiesAndEquity.totalLiabilities)}</span>
                    </div>

                    {/* Total L + E */}
                    <div className="bg-gradient-to-r from-pink-200  to-violet-200 border-t-2 border-pink-300 px-4 py-3 flex justify-between font-bold text-base text-rose-800">
                      <span>{t("TOTAL LIABILITIES & EQUITY")}</span>
                      <span>{formatCurrency(currency, balanceSheet.liabilitiesAndEquity.totalLiabilitiesAndEquity)}</span>
                    </div>
                  </div>
                </div>

                {/* ── Balance check note ── */}
                <div className={`mt-4 p-3 rounded-lg text-xs font-medium flex items-center gap-2 ${Math.abs(balanceSheet.assets.totalAssets - balanceSheet.liabilitiesAndEquity.totalLiabilitiesAndEquity) < 1
                    ? "bg-green-50 text-green-700"
                    : "bg-amber-50 text-amber-700"
                  }`}>
                  <span>{
                    Math.abs(balanceSheet.assets.totalAssets - balanceSheet.liabilitiesAndEquity.totalLiabilitiesAndEquity) < 1
                      ? t("✓ Balance sheet is balanced — Assets = Liabilities + Equity")
                      : t("⚠ Difference noted: some transactions may not yet be categorized")
                  }</span>
                  <span className="ml-auto text-gray-400">{t("All figures in")} {currency || "BHD"}</span>
                </div>
              </>
            )}
          </div>
        </TabPane>

        <TabPane tab={t("CASH FLOW")} key="3">
          <div className="pdf-section bg-white p-0 text-gray-500">
            <div className="flex justify-between items-center border-b pb-4 mb-4">
              <div className="flex items-center gap-3">
                <div className="w-14 h-14 rounded-2xl bg-purple-100 flex items-center justify-center">
                  <FilePdfOutlined className="text-purple-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold">
                    {t("Cash Statement")}
                  </h2>
                  <p className="text-sm text-gray-500">
                    {t("Report Period")}: {reportPeriod} •{" "}
                    {t("Generated")}: {generatedAt}
                  </p>
                </div>
              </div>
              <div className="flex gap-2 items-center">
                <button
                  onClick={() => handleDownloadPDF("cashflow")}
                  className="no-print btn-gradient flex items-center gap-2 px-3 py-1 text-white rounded-lg text-xs"
                >
                  <FilePdfOutlined />
                  Download PDF
                </button>

                <span className="text-green-600 font-semibold text-xs">
                  REAL-TIME SYNC
                </span>
              </div>
            </div>
            {cashFlowLoading ? (
              <TableShimmer columns={8} rows={10} />
            ) : (
              <Table
                dataSource={cashFlows}
                columns={columns}
                rowKey="_id"
                pagination={false}
                rowClassName="hover:bg-gray-50 transition-colors"
                components={{
                  body: {
                    row: (props) => <tr {...props} style={{ height: "60px" }} />,
                  },
                }}
                size="small"
              />
            )}

            <div className="flex justify-end mt-4 pb-2">
              <button
                onClick={() => navigate("/finance/vouchers")}
                className="btn-gradient px-4 py-2 text-white rounded-lg text-sm"
              >
                {t("View All")}
              </button>
            </div>
          </div>
        </TabPane>
      </Tabs>
    </div>
  );
};

export default MainSection;
